// Settings Page with Profile Editing for Iron Forums

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  profilesAPI,
  authAPI,
  notificationsAPI,
  calendarAPI,
  type NotificationPreferences,
} from '../api/client';
import { useAuth } from '../context/useAuth';
import { useLinkedInConnect } from '../hooks/useLinkedInConnect';
import { useCalendarConnect } from '../hooks/useCalendarConnect';
import { 
  Bot,
  User as UserIcon,
  Briefcase,
  MapPin,
  Heart,
  Target,
  TrendingUp,
  BookOpen,
  Bell,
  CheckCircle,
  AlertCircle,
  Loader2,
  Lock,
  Shield,
  Trash2,
  AlertTriangle,
  X,
  CreditCard,
  Sparkles,
  HelpCircle,
  Phone,
  Globe,
  Home,
  Building2,
  Linkedin,
  Calendar as CalendarIcon,
  CalendarCheck,
  RefreshCw,
  Brain,
  type LucideIcon,
} from 'lucide-react';
import { jarvisAPI, paymentsAPI } from '../api/client';
import type {
  UserProfile,
  ProfileVisibilitySettings,
  VisibilityAudience,
  BillingSummary,
  PaymentStatus,
  Gender,
  ForumType,
  CalendarConnection,
  JarvisMemory,
} from '../types';
import TagPicker from '../components/TagPicker';
import AvatarCapture from '../components/AvatarCapture';
import JarvisFieldAssist from '../components/JarvisFieldAssist';
import MicButton from '../components/MicButton';
import { FIELD_ASSIST } from '../components/fieldAssist';
import TwoFactorSettings from '../components/TwoFactorSettings';
import BillingReceipts from '../components/BillingReceipts';
import SupportFeedbackModal from '../components/SupportFeedbackModal';
import { SUPPORT_EMAIL } from '../utils/support';
import { isNativePlatform } from '../native';
import { canSelfServeBilling, shouldExposeWebCheckout } from '../lib/paymentGate';
import { formatPhone } from '../utils/phone';
import { normalizeUrl } from '../utils/url';
import { COUNTRIES } from '../utils/countries';

// US States for dropdown
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const POSITION_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'partner', label: 'Partner' },
  { value: 'other', label: 'Other' },
];

// Business-profiling options — kept identical to ProfileOnboarding so Settings
// edits the same values the wizard collects (must match backend choices in
// users/models.py).
const REVENUE_RANGES = [
  { value: 'under_500k', label: '<$500k' },
  { value: '500k_1m', label: '$500k–$1M' },
  { value: '1m_5m', label: '$1M–$5M' },
  { value: '5m_10m', label: '$5M–$10M' },
  { value: '10m_25m', label: '$10M–$25M' },
  { value: '25m_plus', label: '$25M+' },
];

const GROWTH_RATES = [
  { value: 'flat_declining', label: 'Flat / Declining' },
  { value: '1_10', label: '1–10%' },
  { value: '10_25', label: '10–25%' },
  { value: '25_plus', label: '25%+' },
];

const COMPANY_STRUCTURES = ['Solopreneur', 'LLC', 'S-Corp', 'C-Corp', 'Franchise', 'Family-Owned', 'Nonprofit'];

interface FormData {
  first_name: string;
  last_name: string;
  gender: Gender;
  forum_preference: ForumType | '';
  avatar_url: string;
  bio: string;
  industry: string;
  business_type: string;
  years_in_business: number | null;
  business_age: string;
  revenue_range: string;
  growth_rate: string;
  company_structure: string[];
  purpose_statement: string;
  quarterly_goal: string;
  family_status: string;
  has_children: boolean;
  num_children: number | null;
  areas_of_expertise: string[];
  growth_areas: string[];
  hobbies_interests: string;
  faith_journey: string;
  why_iron_forums: string;
  cell_phone: string;
  title: string;
  website: string;
  additional_websites: string[];
  position: UserProfile['position'];
  spouse_name: string;
  spouse_email: string;
  business_address: string;
  business_city: string;
  business_state: string;
  business_zip: string;
  business_country: string;
  num_staff: number | null;
  home_address: string;
  home_city: string;
  home_state: string;
  home_zip: string;
  home_country: string;
  church_attending: string;
  forum_interest: string;
}

// Account deletion grace period (days). Mirrors backend ACCOUNT_DELETION_GRACE_DAYS.
// 0 = deactivate only; staff restore or permanently delete. No auto-purge timer.
const DELETION_GRACE_DAYS = 0;

const BILLING_BADGES: Record<PaymentStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-700' },
  // Card on file, first/next charge lands on the org billing day (there is
  // no free trial; a member who already paid this month shows Active).
  trialing: { label: 'Signed up', className: 'bg-green-100 text-green-700' },
  past_due: { label: 'Past due', className: 'bg-amber-100 text-amber-700' },
  pending: { label: 'Pending', className: 'bg-gray-100 text-gray-600' },
  canceled: { label: 'Canceled', className: 'bg-gray-200 text-gray-600' },
  failed: { label: 'Payment failed', className: 'bg-red-100 text-red-700' },
  unpaid: { label: 'Not started', className: 'bg-gray-100 text-gray-600' },
};

function formatBillingDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

type SettingsTab = 'profile' | 'billing' | 'account';

const SETTINGS_TABS: { key: SettingsTab; label: string; icon: LucideIcon }[] = [
  { key: 'profile', label: 'Profile', icon: UserIcon },
  { key: 'billing', label: 'Membership & Billing', icon: CreditCard },
  { key: 'account', label: 'Account', icon: Shield },
];

// Sections within the Profile tab, in page order. The keys double as the legacy
// ?tab= values from when each section was its own tab, so old deep links (and
// Jarvis's older answers) still land in the right place.
const PROFILE_SECTIONS: { key: string; label: string }[] = [
  { key: 'basics', label: 'Basics' },
  { key: 'about', label: 'About You' },
  { key: 'professional', label: 'Professional' },
  { key: 'family', label: 'Family' },
  { key: 'expertise', label: 'Expertise' },
];

function resolveTabParam(tabParam: string | null): { tab: SettingsTab; section: string | null } {
  if (tabParam === 'billing' || tabParam === 'account') {
    return { tab: tabParam, section: null };
  }
  if (PROFILE_SECTIONS.some((s) => s.key === tabParam)) return { tab: 'profile', section: tabParam };
  // ?tab=purpose deep-links straight to the purpose-statement box, which sits
  // below the fold of the Professional card (used by the dashboard banner).
  if (tabParam === 'purpose') return { tab: 'profile', section: 'purpose' };
  return { tab: 'profile', section: null };
}

export default function Settings() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialParam = resolveTabParam(searchParams.get('tab'));
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialParam.tab);
  // Legacy ?tab=professional-style deep links scroll to that section once loaded.
  const pendingSectionRef = useRef<string | null>(initialParam.section);

  const scrollToSection = (key: string) => {
    document.getElementById(`profile-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const selectTab = (key: SettingsTab) => {
    setActiveTab(key);
    setSearchParams(
      (prev) => {
        prev.set('tab', key);
        return prev;
      },
      { replace: true }
    );
  };

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [, setProfile] = useState<UserProfile | null>(null);

  // Account deletion state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    gender: '',
    forum_preference: '',
    avatar_url: '',
    bio: '',
    industry: '',
    business_type: '',
    years_in_business: null,
    business_age: '',
    revenue_range: '',
    growth_rate: '',
    company_structure: [],
    purpose_statement: '',
    quarterly_goal: '',
    family_status: '',
    has_children: false,
    num_children: null,
    areas_of_expertise: [],
    growth_areas: [],
    hobbies_interests: '',
    faith_journey: '',
    why_iron_forums: '',
    cell_phone: '',
    title: '',
    website: '',
    additional_websites: [],
    position: '',
    spouse_name: '',
    spouse_email: '',
    business_address: '',
    business_city: '',
    business_state: '',
    business_zip: '',
    business_country: '',
    num_staff: null,
    home_address: '',
    home_city: '',
    home_state: '',
    home_zip: '',
    home_country: '',
    church_attending: '',
    forum_interest: '',
  });
  const [assistField, setAssistField] = useState<string | null>(null);
  const [linkedinPhotoUrl, setLinkedinPhotoUrl] = useState<string>('');
  const {
    connect: connectLinkedIn,
    status: linkedinStatus,
    profile: linkedinProfile,
    statusMessage: linkedinStatusMessage,
  } = useLinkedInConnect();

  // Calendar connections (Google / Microsoft two-way sync).
  const [calendarConnections, setCalendarConnections] = useState<CalendarConnection[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const fetchCalendarConnections = useCallback(async () => {
    try {
      const data = await calendarAPI.getConnections();
      setCalendarConnections(data);
    } catch {
      setCalendarConnections([]);
    } finally {
      setCalendarLoading(false);
    }
  }, []);
  const { connectingProvider, calendarUnavailable, connectCalendar } = useCalendarConnect(
    fetchCalendarConnections,
  );

  const [visibility, setVisibility] = useState<ProfileVisibilitySettings>({
    bio_visibility: 'all',
    business_visibility: 'all',
    family_visibility: 'all',
    faith_visibility: 'all',
    expertise_visibility: 'all',
    growth_visibility: 'private',
    contact_visibility: 'all',
  });
  const [visibilitySaving, setVisibilitySaving] = useState(false);

  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences>({
    push_enabled: true,
    direct_messages: true,
    group_messages: true,
    commitment_reminders: true,
    snapshot_reminders: true,
    jarvis_reflections: true,
    meeting_requests: true,
    broadcasts: true,
  });
  const [notifSaving, setNotifSaving] = useState(false);

  const [billing, setBilling] = useState<BillingSummary | null>(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [coverFeeDraft, setCoverFeeDraft] = useState(true);
  const [feeSaving, setFeeSaving] = useState(false);

  // Ask IF long-term memory (facts Jarvis has learned from past conversations)
  const [memories, setMemories] = useState<JarvisMemory[]>([]);
  const [memoriesLoading, setMemoriesLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
    fetchVisibility();
    fetchNotifPrefs();
    fetchBilling();
    fetchCalendarConnections();
    fetchMemories();
  }, []);

  useEffect(() => {
    if (!linkedinProfile) return;
    setFormData((previous) => ({
      ...previous,
      first_name: previous.first_name || linkedinProfile.first_name,
      last_name: previous.last_name || linkedinProfile.last_name,
    }));
    if (linkedinProfile.picture) setLinkedinPhotoUrl(linkedinProfile.picture);
  }, [linkedinProfile]);

  const fetchMemories = async () => {
    try {
      const data = await jarvisAPI.getMemories();
      setMemories(data);
    } catch {
      setMemories([]);
    } finally {
      setMemoriesLoading(false);
    }
  };

  const deleteMemory = async (id: number) => {
    try {
      await jarvisAPI.deleteMemory(id);
      setMemories((prev) => prev.filter((m) => m.id !== id));
      toast.success('Forgotten.');
    } catch {
      toast.error('Could not delete that memory. Please try again.');
    }
  };

  const disconnectCalendar = async (connection: CalendarConnection) => {
    try {
      await calendarAPI.disconnect(connection.id);
      setCalendarConnections((prev) => prev.filter((c) => c.id !== connection.id));
      toast.success(`Disconnected ${connection.provider_label}.`);
    } catch {
      toast.error('Could not disconnect. Please try again.');
    }
  };

  const fetchBilling = async () => {
    try {
      const data = await paymentsAPI.getBillingSummary();
      setBilling(data);
      setCoverFeeDraft(data.covers_processing_fee !== false);
    } catch {
      setBilling(null);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleSaveProcessingFee = async () => {
    setFeeSaving(true);
    try {
      await paymentsAPI.setProcessingFee(coverFeeDraft);
      await fetchBilling();
      toast.success(
        coverFeeDraft
          ? 'Thanks — the 3% processing fee will be added to your next renewal.'
          : 'Got it — we will not add the processing fee.',
      );
    } catch {
      toast.error('Could not update the processing fee. Please try again.');
    } finally {
      setFeeSaving(false);
    }
  };

  const handleOpenStripe = async (flow?: 'payment_method_update') => {
    setPortalLoading(true);
    try {
      const res = await paymentsAPI.createPortalSession(flow);
      if (res.url) {
        window.location.href = res.url;
        return;
      }
      if (res.reason === 'payment_not_required') {
        toast.error('No payment is required for your membership.');
        return;
      }
      if (res.reason === 'no_customer') {
        toast.error('Complete your first payment before managing a card.');
        navigate('/payment');
        return;
      }
      toast.error(res.error || 'Could not open Stripe. Please try again.');
    } catch {
      toast.error('Could not open Stripe. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const data = await profilesAPI.getMyProfile();
      setProfile(data);
      setFormData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        gender: data.gender || '',
        forum_preference: data.forum_preference || '',
        avatar_url: data.avatar_url || '',
        bio: data.bio || '',
        industry: data.industry || '',
        business_type: data.business_type || '',
        years_in_business: data.years_in_business,
        business_age: data.business_age || '',
        revenue_range: data.revenue_range || '',
        growth_rate: data.growth_rate || '',
        company_structure: data.company_structure || [],
        purpose_statement: data.purpose_statement || '',
        quarterly_goal: data.quarterly_goal || '',
        family_status: data.family_status || '',
        has_children: data.has_children || false,
        num_children: data.num_children,
        areas_of_expertise: data.areas_of_expertise || [],
        growth_areas: data.growth_areas || [],
        hobbies_interests: data.hobbies_interests || '',
        faith_journey: data.faith_journey || '',
        why_iron_forums: data.why_iron_forums || '',
        cell_phone: data.cell_phone || '',
        title: data.title || '',
        website: data.website || '',
        additional_websites: data.additional_websites || [],
        position: data.position || '',
        spouse_name: data.spouse_name || '',
        spouse_email: data.spouse_email || '',
        business_address: data.business_address || '',
        business_city: data.business_city || '',
        business_state: data.business_state || '',
        business_zip: data.business_zip || '',
        business_country: data.business_country || '',
        num_staff: data.num_staff,
        home_address: data.home_address || '',
        home_city: data.home_city || '',
        home_state: data.home_state || '',
        home_zip: data.home_zip || '',
        home_country: data.home_country || '',
        church_attending: data.church_attending || '',
        forum_interest: data.forum_interest || '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVisibility = async () => {
    try {
      const data = await profilesAPI.getVisibility();
      setVisibility(data);
    } catch {
      // Use defaults
    }
  };

  const saveVisibility = async (updated: Partial<ProfileVisibilitySettings>) => {
    setVisibilitySaving(true);
    const newVisibility = { ...visibility, ...updated };
    setVisibility(newVisibility);
    try {
      await profilesAPI.updateVisibility(newVisibility);
    } catch {
      // Revert on error
      fetchVisibility();
    } finally {
      setVisibilitySaving(false);
    }
  };

  const fetchNotifPrefs = async () => {
    try {
      const data = await notificationsAPI.getPreferences();
      setNotifPrefs(data);
    } catch {
      // Use defaults
    }
  };

  const saveNotifPrefs = async (updated: Partial<NotificationPreferences>) => {
    setNotifSaving(true);
    const previous = notifPrefs;
    const next = { ...notifPrefs, ...updated };
    setNotifPrefs(next);
    try {
      await notificationsAPI.updatePreferences(updated);
    } catch {
      setNotifPrefs(previous); // revert on error
    } finally {
      setNotifSaving(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: value ? parseInt(value) : null }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const saveProfile = async () => {
    setIsSaving(true);
    setSaveStatus('idle');

    try {
      await profilesAPI.updateMyProfile({
        ...formData,
        website: normalizeUrl(formData.website),
        additional_websites: formData.additional_websites
          .map((site) => normalizeUrl(site))
          .filter(Boolean),
      });
      setSaveStatus('success');

      // Refresh the auth context to update user data
      if (refreshUser) {
        await refreshUser();
      }

      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-save the profile shortly after the member stops editing. We skip the
  // initial population from fetchProfile (and anything before the load finishes)
  // so we don't fire a redundant save on first render.
  const skipNextAutoSave = useRef(true);
  useEffect(() => {
    if (isLoading) return;
    if (skipNextAutoSave.current) {
      skipNextAutoSave.current = false;
      return;
    }
    const timer = setTimeout(() => {
      void saveProfile();
    }, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, isLoading]);

  useEffect(() => {
    if (isLoading || !pendingSectionRef.current) return;
    const section = pendingSectionRef.current;
    pendingSectionRef.current = null;
    requestAnimationFrame(() => scrollToSection(section));
  }, [isLoading]);

  const deletionScheduled = !!user?.deletion_requested_at;

  const scheduledPurgeDate = (() => {
    if (!user?.deletion_requested_at || DELETION_GRACE_DAYS <= 0) return null;
    const d = new Date(user.deletion_requested_at);
    d.setDate(d.getDate() + DELETION_GRACE_DAYS);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  })();

  const handleRequestDeletion = async () => {
    setDeleteLoading(true);
    try {
      await authAPI.requestAccountDeletion();
      toast.success('Your account has been deactivated. You will be signed out now.');
      setShowDeleteModal(false);
      // Tokens are blacklisted server-side; clear local session and return to login.
      await logout();
      navigate('/login');
    } catch {
      toast.error('Could not process the request. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setCancelLoading(true);
    try {
      await authAPI.cancelAccountDeletion();
      toast.success('Account deletion cancelled. Welcome back!');
      if (refreshUser) {
        await refreshUser();
      }
    } catch {
      toast.error('Could not cancel deletion. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20';

  const applyAssistDraft = (draft: string) => {
    if (assistField) {
      const field = assistField;
      setFormData(prev => ({ ...prev, [field]: draft }));
    }
    setAssistField(null);
  };

  // Mirrors the onboarding "Help from Jarvis" label row: a hover hint plus an
  // optional trigger that drafts the field from the member's own answers.
  const AssistLabel = ({ field, label }: { field: string; label: string }) => {
    const cfg = FIELD_ASSIST[field];
    return (
      <div className="flex items-center justify-between mb-1">
        <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
          {label}
          {cfg && (
            <span title={cfg.hint} className="text-gray-400 cursor-help">
              <HelpCircle size={14} />
            </span>
          )}
        </label>
        {cfg && (
          <button
            type="button"
            onClick={() => setAssistField(field)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gold hover:text-gold-dark transition"
            title="Ask IF asks a few questions, then drafts this from your answers"
          >
            <Sparkles size={14} />
            Help from Ask IF
          </button>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy">
              Settings
            </h1>
            <p className="text-gray-600 mt-1">
              Manage your profile and account settings
            </p>
          </div>
          <Link
            to="/jarvis?mode=onboarding"
            className="flex items-center gap-2 px-4 py-2 border border-navy text-navy rounded-lg hover:bg-off-white transition"
          >
            <Bot size={18} />
            Edit with Ask IF
          </Link>
        </div>
      </div>

      {/* Deletion scheduled banner */}
      {deletionScheduled && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-base font-semibold text-red-700">
                  Your account is marked for deletion
                </h2>
                <p className="text-sm text-red-600 mt-1">
                  {scheduledPurgeDate
                    ? `Your account and all of your data will be permanently deleted on ${scheduledPurgeDate}. `
                    : 'Your account is deactivated. An admin can restore it or permanently delete it. '}
                  Changed your mind? You can cancel and keep your account.
                </p>
              </div>
            </div>
            <button
              onClick={handleCancelDeletion}
              disabled={cancelLoading}
              className="flex-shrink-0 flex items-center justify-center gap-2 px-5 py-2.5 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50"
            >
              {cancelLoading ? <Loader2 className="animate-spin" size={18} /> : null}
              Cancel deletion
            </button>
          </div>
        </div>
      )}

      {/* Sub-tab navigation */}
      <div className="bg-white rounded-2xl p-2 shadow-sm">
        <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Settings sections">
          {SETTINGS_TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activeTab === key}
              onClick={() => selectTab(key)}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium whitespace-nowrap transition ${
                activeTab === key
                  ? 'bg-navy text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Profile + account panels — profile fields save automatically (no manual submit) */}
      <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
        {/* Profile — all sections stacked, with anchor chips for quick jumps */}
        {activeTab === 'profile' && (
        <>
        <div className="flex flex-wrap gap-2 px-1">
          {PROFILE_SECTIONS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => scrollToSection(key)}
              className="px-3 py-1.5 rounded-full text-sm font-medium bg-white text-gray-600 shadow-sm hover:text-navy transition"
            >
              {label}
            </button>
          ))}
        </div>

        {/* Basics (personal) */}
        <div id="profile-basics" className="bg-white rounded-2xl p-6 shadow-sm scroll-mt-4">
          <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
            <UserIcon size={20} />
            Basics
          </h2>

          <div className="space-y-5">
            {/* Connect LinkedIn — pre-fills name and offers the LinkedIn photo. */}
            <div className="rounded-xl border border-[#0a66c2]/25 bg-[#0a66c2]/5 p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Linkedin size={18} className="text-[#0a66c2]" />
                  <span className="text-sm font-medium text-navy">Connect LinkedIn</span>
                </div>
                <button
                  type="button"
                  onClick={connectLinkedIn}
                  disabled={linkedinStatus === 'connecting' || linkedinStatus === 'connected'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-[#0a66c2] text-white hover:bg-[#085397] transition disabled:opacity-60"
                >
                  {linkedinStatus === 'connecting' ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <Linkedin size={15} />
                  )}
                  {linkedinStatus === 'connected' ? 'Connected' : 'Connect'}
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                {linkedinStatusMessage}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo</label>
              <AvatarCapture
                avatarUrl={formData.avatar_url}
                onChange={(url) => setFormData(prev => ({ ...prev, avatar_url: url }))}
                linkedinPhotoUrl={linkedinPhotoUrl}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone size={14} className="inline mr-1" />
                  Cell
                </label>
                <input
                  type="tel"
                  name="cell_phone"
                  inputMode="numeric"
                  maxLength={12}
                  value={formData.cell_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, cell_phone: formatPhone(e.target.value) }))}
                  placeholder="123-456-7890"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className={`${inputClass} bg-gray-50 text-gray-500`}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferred Forum type
              </label>
              <select
                name="forum_preference"
                value={formData.forum_preference}
                onChange={handleInputChange}
                className={inputClass}
              >
                <option value="">Select...</option>
                <option value="mens">Men's Forum</option>
                <option value="womens">Women's Forum</option>
                <option value="mixed">Combined Forum</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Choose the Forum setting you prefer. An administrator can update your assignment later.
              </p>
            </div>

            {/* Home location (personal) */}
            <div className="pt-2 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-navy mb-3 flex items-center gap-1.5">
                <Home size={15} /> Home Location
              </h3>
              <div className="space-y-4">
                <input
                  type="text"
                  name="home_address"
                  value={formData.home_address}
                  onChange={handleInputChange}
                  placeholder="Street address"
                  autoComplete="street-address"
                  className={inputClass}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="home_city"
                    value={formData.home_city}
                    onChange={handleInputChange}
                    placeholder="City"
                    className={inputClass}
                  />
                  <select
                    name="home_state"
                    value={formData.home_state}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="">State...</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="home_zip"
                    value={formData.home_zip}
                    onChange={handleInputChange}
                    placeholder="ZIP / Postal Code"
                    className={inputClass}
                  />
                  <select
                    name="home_country"
                    value={formData.home_country}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="">Select country...</option>
                    {formData.home_country && !COUNTRIES.includes(formData.home_country) && (
                      <option value={formData.home_country}>{formData.home_country}</option>
                    )}
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Spouse (personal) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Spouse Name</label>
                <input
                  type="text"
                  name="spouse_name"
                  value={formData.spouse_name}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Spouse Email</label>
                <input
                  type="email"
                  name="spouse_email"
                  value={formData.spouse_email}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Which Forum are you interested in joining?
              </label>
              <input
                type="text"
                name="forum_interest"
                value={formData.forum_interest}
                onChange={handleInputChange}
                placeholder="e.g., GA - North Gwinnett - Men's Forum"
                className={inputClass}
              />
              <p className="text-xs text-gray-500 mt-1">
                Your facilitator will confirm your Forum assignment.
              </p>
            </div>
          </div>
        </div>

        {/* About You */}
        <div id="profile-about" className="bg-white rounded-2xl p-6 shadow-sm scroll-mt-4">
          <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
            <BookOpen size={20} />
            About You
          </h2>

          <div className="space-y-4">
            <div>
              <AssistLabel field="bio" label="Bio" />
              <div className="relative">
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Tell other members about yourself, or use Help from Ask IF..."
                  className={`${inputClass} pr-12`}
                />
                <MicButton
                  value={formData.bio}
                  onChange={(next) => setFormData(prev => ({ ...prev, bio: next }))}
                  className="absolute bottom-2 right-2"
                />
              </div>
            </div>
            <div>
              <AssistLabel field="faith_journey" label="Faith Journey" />
              <div className="relative">
                <textarea
                  name="faith_journey"
                  value={formData.faith_journey}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Share your faith journey..."
                  className={`${inputClass} pr-12`}
                />
                <MicButton
                  value={formData.faith_journey}
                  onChange={(next) => setFormData(prev => ({ ...prev, faith_journey: next }))}
                  className="absolute bottom-2 right-2"
                />
              </div>
            </div>
            <div>
              <AssistLabel
                field="why_iron_forums"
                label="Why are you seeking Christian business community?"
              />
              <div className="relative">
                <textarea
                  name="why_iron_forums"
                  value={formData.why_iron_forums}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="What are you looking for in a community like this?"
                  className={`${inputClass} pr-12`}
                />
                <MicButton
                  value={formData.why_iron_forums}
                  onChange={(next) => setFormData(prev => ({ ...prev, why_iron_forums: next }))}
                  className="absolute bottom-2 right-2"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Professional */}
        <div id="profile-professional" className="bg-white rounded-2xl p-6 shadow-sm scroll-mt-4">
          <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
            <Briefcase size={20} />
            Professional
          </h2>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., CEO, Founder"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  className={inputClass}
                >
                  <option value="">Select...</option>
                  {POSITION_OPTIONS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Globe size={14} className="inline mr-1" />
                Website
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://"
                className={inputClass}
              />
              <p className="mt-2 text-xs text-gray-500">
                Own more than one business? Add their websites so members can find
                all of them on your profile.
              </p>
              {formData.additional_websites.map((site, index) => (
                <div key={index} className="mt-2 flex items-center gap-2">
                  <input
                    type="url"
                    value={site}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        additional_websites: prev.additional_websites.map(
                          (s, i) => (i === index ? e.target.value : s),
                        ),
                      }))
                    }
                    placeholder="https://"
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        additional_websites: prev.additional_websites.filter(
                          (_, i) => i !== index,
                        ),
                      }))
                    }
                    className="text-gray-400 hover:text-red-600 transition shrink-0"
                    aria-label="Remove website"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              {formData.additional_websites.length < 10 && (
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      additional_websites: [...prev.additional_websites, ''],
                    }))
                  }
                  className="mt-2 text-sm text-gold hover:underline"
                >
                  + Add another website
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Industry <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  placeholder="e.g., Technology, Construction"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
                <input
                  type="text"
                  name="business_type"
                  value={formData.business_type}
                  onChange={handleInputChange}
                  placeholder="e.g., SaaS, General Contractor"
                  className={inputClass}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Years in Business</label>
                <input
                  type="number"
                  name="years_in_business"
                  value={formData.years_in_business ?? ''}
                  onChange={handleInputChange}
                  min="0"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Staff <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  name="num_staff"
                  value={formData.num_staff ?? ''}
                  onChange={handleInputChange}
                  min="0"
                  className={inputClass}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age of Business</label>
                <select
                  name="business_age"
                  value={formData.business_age}
                  onChange={handleInputChange}
                  className={inputClass}
                >
                  <option value="">Select...</option>
                  <option value="startup">Startup</option>
                  <option value="legacy">Legacy</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Top-Line Revenue Range</label>
                <select
                  name="revenue_range"
                  value={formData.revenue_range}
                  onChange={handleInputChange}
                  className={inputClass}
                >
                  <option value="">Select...</option>
                  {REVENUE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Annual Growth Rate</label>
              <select
                name="growth_rate"
                value={formData.growth_rate}
                onChange={handleInputChange}
                className={inputClass}
              >
                <option value="">Select...</option>
                {GROWTH_RATES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Structure
                <span className="font-normal text-gray-500 ml-1">(select all that apply)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {COMPANY_STRUCTURES.map((opt) => {
                  const selected = formData.company_structure.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() =>
                        setFormData(prev => ({
                          ...prev,
                          company_structure: selected
                            ? prev.company_structure.filter((c) => c !== opt)
                            : [...prev.company_structure, opt],
                        }))
                      }
                      className={`tap-target px-3 py-2 rounded-full text-sm font-medium border transition ${
                        selected
                          ? 'bg-navy text-white border-navy'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-navy/40'
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Business Address */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-navy mb-3 flex items-center gap-1.5">
              <Building2 size={15} /> Business Address
            </h3>
            <div className="space-y-4">
              <input
                type="text"
                name="business_address"
                value={formData.business_address}
                onChange={handleInputChange}
                placeholder="Street address"
                className={inputClass}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="business_city"
                  value={formData.business_city}
                  onChange={handleInputChange}
                  placeholder="City"
                  className={inputClass}
                />
                <select
                  name="business_state"
                  value={formData.business_state}
                  onChange={handleInputChange}
                  className={inputClass}
                >
                  <option value="">Select a state</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="business_zip"
                  value={formData.business_zip}
                  onChange={handleInputChange}
                  placeholder="ZIP / Postal Code"
                  className={inputClass}
                />
                <select
                  name="business_country"
                  value={formData.business_country}
                  onChange={handleInputChange}
                  className={inputClass}
                >
                  <option value="">Select country...</option>
                  {formData.business_country && !COUNTRIES.includes(formData.business_country) && (
                    <option value={formData.business_country}>{formData.business_country}</option>
                  )}
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-4">
            <div id="profile-purpose" className="scroll-mt-4">
              <AssistLabel field="purpose_statement" label="Purpose Statement" />
              <div className="relative">
                <textarea
                  name="purpose_statement"
                  value={formData.purpose_statement}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="What drives your professional mission?"
                  className={`${inputClass} pr-12`}
                />
                <MicButton
                  value={formData.purpose_statement}
                  onChange={(next) => setFormData(prev => ({ ...prev, purpose_statement: next }))}
                  className="absolute bottom-2 right-2"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Season Goal</label>
              <div className="relative">
                <textarea
                  name="quarterly_goal"
                  value={formData.quarterly_goal}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="What are you working toward this season?"
                  className={`${inputClass} pr-12`}
                />
                <MicButton
                  value={formData.quarterly_goal}
                  onChange={(next) => setFormData(prev => ({ ...prev, quarterly_goal: next }))}
                  className="absolute bottom-2 right-2"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Family */}
        <div id="profile-family" className="bg-white rounded-2xl p-6 shadow-sm scroll-mt-4">
          <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
            <Heart size={20} />
            Family
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Family Status</label>
              <select
                name="family_status"
                value={formData.family_status}
                onChange={handleInputChange}
                className={inputClass}
              >
                <option value="">Select...</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option>
              </select>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Do you have children?</label>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, has_children: !prev.has_children }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  formData.has_children ? 'bg-navy' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    formData.has_children ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {formData.has_children && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Children</label>
                <input
                  type="number"
                  name="num_children"
                  value={formData.num_children ?? ''}
                  onChange={handleInputChange}
                  min="0"
                  className={inputClass}
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Church Attending</label>
              <input
                type="text"
                name="church_attending"
                value={formData.church_attending}
                onChange={handleInputChange}
                placeholder="Name of the church you attend"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hobbies & Interests</label>
              <div className="relative">
                <textarea
                  name="hobbies_interests"
                  value={formData.hobbies_interests}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="What do you enjoy outside of work?"
                  className={`${inputClass} pr-12`}
                />
                <MicButton
                  value={formData.hobbies_interests}
                  onChange={(next) => setFormData(prev => ({ ...prev, hobbies_interests: next }))}
                  className="absolute bottom-2 right-2"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Expertise & Growth */}
        <div id="profile-expertise" className="bg-white rounded-2xl p-6 shadow-sm scroll-mt-4">
          <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
            <Target size={20} />
            Expertise & Growth
          </h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Areas of Expertise
                <span className="font-normal text-gray-500 ml-1">(skills you can help others with)</span>
              </label>
              <TagPicker
                selectedTags={formData.areas_of_expertise}
                onChange={(tags) => setFormData(prev => ({ ...prev, areas_of_expertise: tags }))}
                placeholder="Search expertise tags or type to add..."
                accentColor="navy"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Growth Areas
                <span className="font-normal text-gray-500 ml-1">(areas you're working to improve)</span>
              </label>
              <TagPicker
                selectedTags={formData.growth_areas}
                onChange={(tags) => setFormData(prev => ({ ...prev, growth_areas: tags }))}
                placeholder="Search growth area tags or type to add..."
                accentColor="gold"
              />
            </div>
          </div>
        </div>

        {/* Auto-save status — profile fields save automatically as you edit */}
        <div
          className="flex items-center justify-end gap-2 text-sm min-h-[20px] px-1"
          aria-live="polite"
        >
          {isSaving ? (
            <>
              <Loader2 className="animate-spin text-gray-400" size={16} />
              <span className="text-gray-500">Saving…</span>
            </>
          ) : saveStatus === 'success' ? (
            <>
              <CheckCircle className="text-green-500" size={16} />
              <span className="text-green-600">All changes saved</span>
            </>
          ) : saveStatus === 'error' ? (
            <>
              <AlertCircle className="text-red-500" size={16} />
              <span className="text-red-600">Couldn't save — keep editing to retry</span>
            </>
          ) : (
            <span className="text-gray-400">Changes save automatically</span>
          )}
        </div>
        </>
        )}

        {/* Account: calendar, privacy, notifications, and account deletion */}
        {activeTab === 'account' && (
        <div className="space-y-6">
        <TwoFactorSettings />
        {/* Calendar connections — two-way sync of forum events */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-navy mb-1 flex items-center gap-2">
            <CalendarIcon size={20} />
            Calendar
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Connect your calendar to automatically add your Forum meetings and keep
            them in sync. We only add your Forum events — we never read your personal
            calendar.
          </p>

          {calendarLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <Loader2 size={16} className="animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="space-y-3">
              {([
                // Google Calendar connect is hidden while Google's OAuth app
                // verification is pending (members were hitting the scary
                // "Google hasn't verified this app" screen). Flip comingSoon
                // off once the Verification Center shows approved.
                { provider: 'google' as const, label: 'Google Calendar', accent: '#4285F4', comingSoon: true },
                { provider: 'microsoft' as const, label: 'Outlook Calendar', accent: '#0078D4', comingSoon: false },
              ]).map(({ provider, label, accent, comingSoon }) => {
                const connection = calendarConnections.find((c) => c.provider === provider);
                const unavailable = calendarUnavailable[provider];
                const connecting = connectingProvider === provider;
                return (
                  <div
                    key={provider}
                    className="rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-3 flex-wrap"
                  >
                    <div className="flex items-start gap-3">
                      <CalendarCheck size={18} style={{ color: accent }} className="mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-navy">{label}</p>
                        {connection ? (
                          <p className="text-xs text-gray-500">
                            {connection.account_email || 'Connected'}
                            {connection.status !== 'connected' && (
                              <span className="ml-1 text-amber-600">
                                ({connection.status === 'expired'
                                  ? 'reconnect needed'
                                  : connection.status})
                              </span>
                            )}
                            {connection.last_synced_at && connection.status === 'connected' && (
                              <span className="ml-1 inline-flex items-center gap-1 text-gray-400">
                                <RefreshCw size={11} />
                                synced {new Date(connection.last_synced_at).toLocaleDateString()}
                              </span>
                            )}
                          </p>
                        ) : comingSoon ? (
                          <p className="text-xs text-gray-500">
                            Coming soon. In the meantime, the “Add to Calendar”
                            button on each event works great.
                          </p>
                        ) : unavailable ? (
                          <p className="text-xs text-gray-500">
                            Calendar connect isn’t enabled yet. The “Add to Calendar”
                            button still works on each event.
                          </p>
                        ) : (
                          <p className="text-xs text-gray-500">Not connected</p>
                        )}
                      </div>
                    </div>
                    {connection ? (
                      <button
                        type="button"
                        onClick={() => disconnectCalendar(connection)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                      >
                        Disconnect
                      </button>
                    ) : comingSoon ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full bg-gold/15 text-gold-dark">
                        Coming soon
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => connectCalendar(provider)}
                        disabled={connecting || unavailable}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-navy text-white hover:bg-navy-dark transition disabled:opacity-60"
                      >
                        {connecting ? (
                          <Loader2 size={15} className="animate-spin" />
                        ) : (
                          <CalendarIcon size={15} />
                        )}
                        Connect
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Privacy Settings */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-navy mb-1 flex items-center gap-2">
            <Shield size={20} />
            Privacy Settings
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Control who can see each section of your profile. Changes save automatically.
          </p>

          {visibilitySaving && (
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
              <Loader2 size={14} className="animate-spin" />
              Saving...
            </div>
          )}

          <div className="space-y-3">
            {([
              { key: 'bio_visibility' as const, label: 'Bio & Interests', icon: UserIcon },
              { key: 'business_visibility' as const, label: 'Business & Career', icon: Briefcase },
              { key: 'family_visibility' as const, label: 'Family', icon: Heart },
              { key: 'faith_visibility' as const, label: 'Faith & Journey', icon: BookOpen },
              { key: 'expertise_visibility' as const, label: 'Areas of Expertise', icon: Target },
              { key: 'growth_visibility' as const, label: 'Growth Areas', icon: TrendingUp },
              { key: 'contact_visibility' as const, label: 'Location', icon: MapPin },
            ]).map(({ key, label, icon: Icon }) => (
              <div key={key} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-3">
                  <Icon size={16} className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  {visibility[key] !== 'all' && (
                    <Lock size={12} className="text-gold" />
                  )}
                </div>
                <select
                  value={visibility[key]}
                  onChange={(e) => saveVisibility({ [key]: e.target.value as VisibilityAudience })}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
                >
                  <option value="all">All Members</option>
                  <option value="forum">My Forum Only</option>
                  <option value="group">My Groups Only</option>
                  <option value="private">Only Me</option>
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-navy mb-1 flex items-center gap-2">
            <Bell size={20} />
            Notifications
          </h2>
        <p className="text-sm text-gray-500 mb-4">
          Choose which push notifications you'd like to receive on your device.
          Changes save automatically.
        </p>

        {notifSaving && (
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
            <Loader2 size={14} className="animate-spin" />
            Saving...
          </div>
        )}

        {/* Master toggle */}
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div>
            <span className="text-sm font-semibold text-gray-800">All push notifications</span>
            <p className="text-xs text-gray-500">Turn everything on or off at once.</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={notifPrefs.push_enabled}
            onClick={() => saveNotifPrefs({ push_enabled: !notifPrefs.push_enabled })}
            className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full transition tap-target ${notifPrefs.push_enabled ? 'bg-navy' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition mt-0.5 ${notifPrefs.push_enabled ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </div>

        <div className={`space-y-1 mt-2 ${notifPrefs.push_enabled ? '' : 'opacity-50 pointer-events-none'}`}>
          {([
            { key: 'direct_messages' as const, label: 'Direct messages', desc: 'When someone sends you a message' },
            { key: 'group_messages' as const, label: 'Group messages', desc: 'Activity in your group threads' },
            { key: 'commitment_reminders' as const, label: 'Grow Goal reminders', desc: 'When a Grow Goal is due soon' },
            { key: 'snapshot_reminders' as const, label: 'Snapshot reminders', desc: 'When your monthly Snapshot is due' },
            { key: 'jarvis_reflections' as const, label: 'Ask IF reflections', desc: "When Ask IF notices something worth reflecting on" },
            { key: 'meeting_requests' as const, label: 'Meeting requests', desc: 'When someone requests a 1:1 meeting or responds to yours' },
            { key: 'broadcasts' as const, label: 'Announcements', desc: 'Push for forum and organization announcements. They still appear on Home.' },
          ]).map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
              <div>
                <span className="text-sm font-medium text-gray-700">{label}</span>
                <p className="text-xs text-gray-500">{desc}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={notifPrefs[key]}
                onClick={() => saveNotifPrefs({ [key]: !notifPrefs[key] })}
                className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full transition tap-target ${notifPrefs[key] ? 'bg-navy' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition mt-0.5 ${notifPrefs[key] ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
          ))}
        </div>
        </div>
        </div>
        )}

        {/* Membership & Billing */}
        {activeTab === 'billing' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-navy mb-1 flex items-center gap-2">
            <CreditCard size={20} />
            Membership & Billing
          </h2>
        <p className="text-sm text-gray-500 mb-4">
          Your membership plan and payment details.
        </p>

        {!shouldExposeWebCheckout(isNativePlatform) ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Membership billing is managed directly by Iron Forums outside this app.
              Contact support if you need help with your membership.
            </p>
            {billing?.has_customer && <BillingReceipts />}
          </div>
        ) : billingLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
            <Loader2 size={16} className="animate-spin" />
            Loading…
          </div>
        ) : !billing ? (
          <p className="text-sm text-gray-500">Couldn't load your billing details right now.</p>
        ) : !canSelfServeBilling(user, billing) ? (
          <p className="text-sm text-gray-500">
            No payment is required for your membership.
            Contact support if you need help.
          </p>
        ) : (
          (() => {
            const showBadge = billing.is_billable || billing.has_subscription;
            const badge = BILLING_BADGES[billing.payment_status] ?? BILLING_BADGES.unpaid;
            const periodEnd = formatBillingDate(billing.current_period_end);
            const ending = billing.payment_status === 'canceled' || billing.cancel_at_period_end === true;
            const dateLine = periodEnd
              ? ending
                ? `Access ends on ${periodEnd}`
                : (billing.payment_status === 'active' || billing.payment_status === 'trialing')
                  ? `Renews on ${periodEnd}`
                  : null
              : null;

            return (
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-navy">{billing.plan_name || 'Membership'}</p>
                    {billing.plan_price && (
                      <p className="text-sm text-gray-500">{billing.plan_price}</p>
                    )}
                  </div>
                  {showBadge && (
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${badge.className}`}>
                      {badge.label}
                    </span>
                  )}
                </div>

                {dateLine && <p className="text-sm text-gray-600">{dateLine}</p>}

                <div className={`rounded-xl px-4 py-3 space-y-3 ${
                  billing.covers_processing_fee == null
                    ? 'border border-gold/40 bg-gold/5'
                    : 'border border-gray-200'
                }`}>
                  <p className="text-sm font-semibold text-navy">
                    Card processing fee
                  </p>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={coverFeeDraft}
                      onChange={(event) => setCoverFeeDraft(event.target.checked)}
                      className="mt-1 rounded border-gray-300 text-navy focus:ring-gold"
                    />
                    <span className="text-sm text-gray-700">
                      Add {billing.processing_fee_percent || '3'}%
                      {billing.processing_fee_price ? ` (${billing.processing_fee_price})` : ''}
                      {' '}to cover card processing so Iron Forums receives the full dues amount.
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => void handleSaveProcessingFee()}
                    disabled={feeSaving || coverFeeDraft === billing.covers_processing_fee}
                    className="px-4 py-2 bg-navy text-white text-sm font-semibold rounded-lg hover:bg-navy-dark transition disabled:opacity-50"
                  >
                    {feeSaving ? 'Saving…' : billing.covers_processing_fee == null ? 'Confirm' : 'Update'}
                  </button>
                </div>

                {billing.payment_status === 'past_due' && billing.has_subscription && (
                  <p className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                    Your last dues payment was declined. Stripe will retry it automatically —
                    please update your card so your membership stays active.
                  </p>
                )}

                {shouldExposeWebCheckout(isNativePlatform)
                  && (billing.has_subscription || !billing.requires_payment) && (
                  <div className="rounded-xl border border-gray-200 px-4 py-3 space-y-3">
                    <p className="text-sm font-semibold text-navy">Payment method</p>
                    {billing.card_brand && billing.card_last4 ? (
                      <p className="text-sm text-gray-600 flex items-center gap-1.5">
                        <CreditCard size={14} className="text-gray-400" />
                        {billing.card_brand} ···· {billing.card_last4}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500">
                        Add or change the card Stripe uses for your membership dues.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => void handleOpenStripe('payment_method_update')}
                        disabled={portalLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-navy text-white text-sm font-semibold rounded-lg hover:bg-navy-dark transition disabled:opacity-50"
                      >
                        {portalLoading ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                        Update credit card
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleOpenStripe()}
                        disabled={portalLoading}
                        className="flex items-center gap-2 px-4 py-2 border border-navy text-navy text-sm font-semibold rounded-lg hover:bg-off-white transition disabled:opacity-50"
                      >
                        Manage billing
                      </button>
                    </div>
                  </div>
                )}

                {billing.has_customer && <BillingReceipts />}

                <div className="pt-1">
                  {billing.requires_payment ? (
                    <button
                      onClick={() => navigate('/payment')}
                      className="flex items-center gap-2 px-5 py-2.5 bg-gold text-white rounded-lg font-semibold hover:bg-gold-dark transition"
                    >
                      <Lock size={18} />
                      Complete payment
                    </button>
                  ) : !billing.is_billable && !billing.has_subscription && !billing.has_customer ? (
                    <p className="text-sm text-gray-500">
                      No payment is required for your membership.
                    </p>
                  ) : null}
                </div>
              </div>
            );
          })()
        )}
        </div>
        )}

        {/* Ask IF long-term memory */}
        {activeTab === 'account' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-navy mb-1 flex items-center gap-2">
            <Brain size={20} />
            What Ask IF Remembers
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Ask IF remembers helpful details you share in conversations — like family
            names or goals you're working toward — so you don't have to repeat
            yourself. Only you can see this list. Remove anything you'd rather it
            forget, or just tell Ask IF "forget that."
          </p>
          {memoriesLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <Loader2 size={16} className="animate-spin" />
              Loading…
            </div>
          ) : memories.length === 0 ? (
            <p className="text-sm text-gray-400">
              Nothing yet — memories appear here as you chat with Ask IF.
            </p>
          ) : (
            <ul className="space-y-2">
              {memories.map((memory) => (
                <li
                  key={memory.id}
                  className="flex items-start justify-between gap-3 rounded-xl border border-gray-200 px-4 py-2.5"
                >
                  <span className="text-sm text-gray-700">{memory.content}</span>
                  <button
                    onClick={() => deleteMemory(memory.id)}
                    className="text-gray-400 hover:text-red-600 transition shrink-0 mt-0.5"
                    aria-label="Forget this"
                    title="Forget this"
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}

        {/* Support & Legal */}
        {activeTab === 'account' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-navy mb-1 flex items-center gap-2">
            <HelpCircle size={20} />
            Support & Feedback
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Something broken? Have a feature idea or a question? Send it straight to the
            Iron Forums team.
          </p>
          <button
            onClick={() => setSupportModalOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition"
          >
            <HelpCircle size={18} />
            Contact support
          </button>
          <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-500 flex flex-wrap items-center gap-x-4 gap-y-1">
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-navy hover:underline">
              {SUPPORT_EMAIL}
            </a>
            <Link to="/requests" className="text-navy hover:underline">Your requests</Link>
            <Link to="/whats-new" className="text-navy hover:underline">What's new</Link>
            <Link to="/security" className="text-navy hover:underline">Security</Link>
            <Link to="/privacy" className="text-navy hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="text-navy hover:underline">Terms of Service</Link>
          </div>
        </div>
        )}

        {/* Danger Zone */}
        {activeTab === 'account' && !deletionScheduled && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-red-100">
          <h2 className="text-lg font-semibold text-red-700 mb-1 flex items-center gap-2">
            <AlertTriangle size={20} />
            Danger Zone
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Deactivate your account. You lose access immediately. An admin can restore
            the account or permanently delete it. There is no automatic deletion timer.
          </p>
          <button
            onClick={() => { setDeleteConfirmText(''); setShowDeleteModal(true); }}
            className="flex items-center gap-2 px-5 py-2.5 border-2 border-red-600 text-red-600 rounded-lg font-semibold hover:bg-red-600 hover:text-white transition"
          >
            <Trash2 size={18} />
            Delete my account
          </button>
        </div>
        )}
      </form>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center justify-center w-11 h-11 bg-red-50 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold text-navy">Delete your account?</h2>
              </div>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600 transition"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3 text-sm text-gray-600 mb-5">
              <p>
                You will be signed out immediately and will not be able to sign back in.
              </p>
              <p>
                Your account is deactivated. An admin can restore it or permanently
                delete it. There is no automatic 30-day timer. To reuse this email
                right away, ask an admin to delete the account permanently from Members.
              </p>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type <span className="font-mono font-semibold text-red-600">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/30 mb-5"
              autoFocus
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition"
              >
                Keep my account
              </button>
              <button
                onClick={handleRequestDeletion}
                disabled={deleteConfirmText !== 'DELETE' || deleteLoading}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? <Loader2 className="animate-spin" size={18} /> : <Trash2 size={18} />}
                Delete account
              </button>
            </div>
          </div>
        </div>
      )}

      {assistField && FIELD_ASSIST[assistField] && (
        <JarvisFieldAssist
          field={assistField}
          title={FIELD_ASSIST[assistField].title}
          intro={FIELD_ASSIST[assistField].intro}
          questions={FIELD_ASSIST[assistField].questions}
          onApply={applyAssistDraft}
          onClose={() => setAssistField(null)}
        />
      )}

      <SupportFeedbackModal isOpen={supportModalOpen} onClose={() => setSupportModalOpen(false)} />
    </div>
  );
}
